import React, { useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from 'react-device-detect';
import SEO from "../components/seo";
import { Text, Flex, Heading, Box } from "rebass";
import Image from "../components/image";
import Bar from "../components/bar";
import Parallax from "../components/parallax";
import { graphql } from "gatsby";
import BackgroundSlide from "../components/backgroundSlide";
import Blur from "../components/blur";
import { pathOr, isEmpty } from "ramda";
import theme from "../gatsby-plugin-theme-ui";
import Modal from "../components/modal";
import { useColorMode } from "theme-ui";
import Fullpage from "../components/fullpage";
import FirstSlide from "../components/firstSlide";
import { useMediaQuery } from "react-responsive";
import throttle from 'lodash.throttle';
import { getRowByLength, profileImagePath } from "../utils/qui-sommes-nous";
import { getComputedTranslateX, useGetFieldsByName } from "../utils";
import QuiSommesNous from "../modules/quiSommesNous";
import useFirstPose from "../hooks/useFirstPose";

const mobileOffset = 5;

const teamMemberPropsMobile = (i) => ({
	m: 0,
	mr: [null, null, 3],
	pt: 0,
	pl: [i % 2 ? 1 : 0],
	pr: [i % 2 ? 0 : 1],
	height: ['40vh', 400],
	mb: [2, null],
	as: "button",
	sx: {
		position: ['relative'],
		top: [i % 2 ? theme.space[mobileOffset] : 0],
	},
});

const IndexPage = ({ data }) => {
	const [activeModal, setActiveModal] = useState(null);
	const teamRef = useRef(null);

	const [colorMode] = useColorMode();
	const { quiSommesNous, parallaxWhite, parallaxBlack, parallaxMobile, profiles, profileSorting: { nodes: { data: sortingData } }, ogImage } = data;
	const isTabletOrMobile = useMediaQuery({ maxWidth: theme.breakpoints[1] }) || isMobile;

	const activeProfile = pathOr([], ['items'], profiles).find(item => item.id === activeModal);

	useEffect(() => {
		if (typeof window !== "undefined" && profiles.items.length && teamRef.current) {
			if (!isTabletOrMobile) {
				const containerRect = { width: null, height: null, };
				const refRect = { width: 0 };
				const ref = teamRef.current;
				const container = ref.parentNode;

				const resizeListener = throttle(() => {
					containerRect.width = container.getBoundingClientRect().width;
					containerRect.height = container.getBoundingClientRect().height;
					const widthOfRow = containerRect.height * 0.29;
					const quantityOfRows = getRowByLength(profiles.items.length);
					const maxWidth = (widthOfRow + theme.space[3]) * quantityOfRows;
					ref.style.width = `${maxWidth}px`;
					refRect.width = maxWidth;
					container.style.alignItems = maxWidth > containerRect.width ? 'flex-start' : 'center';
				});

				resizeListener();
				window.addEventListener('resize', resizeListener);

				// TODO add horizontal scroll hook
				const onWheelListener = e => {
					const maxWidth = refRect.width - containerRect.width;
					const scroll = -getComputedTranslateX(teamRef.current);
					if (!(scroll === 0 && e.deltaY < 0)) {
						e.preventDefault();
						e.stopPropagation();
						const scrollX = scroll + e.deltaY;
						ref.style.transform = `translateX(-${scrollX < 0 ? 0 : (scrollX > maxWidth ? maxWidth : scrollX)}px)`;
					}
				};

				container.addEventListener('wheel', onWheelListener);
				return () => {
					window.removeEventListener('resize', resizeListener);
					container.removeEventListener('wheel', onWheelListener);
				}
			} else {
				const ref = teamRef.current;
				ref.style.transform = null;
				ref.style.width = null;
			}
		}
	}, [teamRef, profiles, isTabletOrMobile]);

	const mappedProfiles = useMemo(
		() => profiles.items
				.map(p => ({ ...p, image: profileImagePath(p, colorMode) }))
				.filter(({ image, langcode }) => !isEmpty(image) && `/${langcode}` === process.env.GATSBY_PATH_PREFIX)
				.sort((a, b) => {
					const indexA = sortingData.findIndex(item => a.id === item.id);
					const indexB = sortingData.findIndex(item => b.id === item.id);
					if (indexA >= 0 && indexB >= 0) {
						return indexA - indexB;
					} else if (indexA >= 0) {
						return -1;
					} else if (indexB >= 0) {
						return 1;
					} else return 0;
				}),
		[profiles, colorMode, sortingData]);

	const firstSlide = useGetFieldsByName(['title', 'subtitle', 'description'], quiSommesNous, colorMode);

	const profileDescription =  pathOr(null, ['relationships', colorMode + 'Description', 'field_j_ai_ete'], activeProfile);
	const pose = useFirstPose();

	return (
		<>
			<SEO title="Qui sommes nous" image={ogImage}/>
			<Fullpage
				background={({ blurred }) => (
					<Blur
						active={blurred}
						sx={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, willChange: 'filter' }}
					>
						<BackgroundSlide
							isQui
							mixedBg={<Parallax layers={isTabletOrMobile ? parallaxMobile.layers : parallaxBlack.layers} disabled={blurred || pose === 'hidden'}/>}

							// defaultBg={isTabletOrMobile ? null : <Parallax layers={parallaxWhite.layers} disabled={blurred || pose === 'hidden'}/>}
							// darkBg={isTabletOrMobile ? null : <Parallax layers={parallaxBlack.layers} disabled={blurred || pose === 'hidden'}/>}
							// mixedBg={isTabletOrMobile ? <Parallax layers={parallaxMobile.layers} disabled={blurred || pose === 'hidden'}/> : null}
						/>
					</Blur>
				)}
				sections={[
					() => <FirstSlide color="white" {...firstSlide} pose={pose} />,
					({ blurred }) => (
						<>
							{isTabletOrMobile ? (
								<Flex
									flexDirection={['row', null, 'column']}
									flexWrap="wrap"
									sx={{ overflow: [null, null, 'hidden'] }}
									height="100%"
									width={['100%', 600, '100%']}
									ref={teamRef}
									pb={[mobileOffset, null, 0]}
								>
									{mappedProfiles.map((profile, i) => (
										<Box
											key={`${profile.id}-preview`}
											width={[0.5, null, '29vh']}
											as="button"
											p={0}
											m={0}
											mr={3}
											onClick={() => setActiveModal(profile.id)}
											{...teamMemberPropsMobile(i)}
										>
											<Image
												height="100%"
												width="100%"
												sx={{ objectFit: 'cover' }}
												fluid={profileImagePath(profile, colorMode)}/>
										</Box>
									))
									}
								</Flex>
								) : (
								<Flex
									flexDirection={['row', null, 'column']}
									flexWrap="wrap"
									sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
									height={`calc(100% - ${theme.space[3] * 2}px)`}
									width="100%"
									pb={[mobileOffset, null, 0]}
								>
									<QuiSommesNous setActiveModal={setActiveModal} active={blurred} mappedProfiles={mappedProfiles} />
								</Flex>
							)}
							<Modal isOpen={!!activeProfile} handleClose={() => setActiveModal(null)}>
								{activeProfile && (
									<Flex
										width={[null, null, 600, 800]}
										alignItems={[null, null, "center"]}
										flexDirection={['column', null, 'row']} sx={{ minWidth: [250, null, 0] }}
									>
										<Image width={[0.5, null, 220, 340]} height={[null, null, 320, 490]}
													 sx={{ objectFit: 'cover', flexShrink: 0, opacity: [0.7, null, 1] }} mr={[null, null, 4]}
													 fluid={profileImagePath(activeProfile, colorMode)}/>
										<Flex
											flexDirection="column"
											justifyContent="center"
											alignItems="flex-start"
											sx={{ textAlign: 'left', position: 'relative' }}
											pt={[4, null, 0]}
										>
											<Flex flexDirection={['column']} sx={{
												position: ['absolute', null, 'static'],
												right: 0,
												bottom: '100%',
											}}>
												<Heading as="h4" fontSize={7}>{activeProfile[colorMode + 'Name']}</Heading>
												<Bar fontSize={4} sx={{ alignSelf: ['flex-end', null, 'flex-start'], width: [null, null, 'auto'] }}
														 fontWeight={700} mt={-2} mb={[null, null, 4]}
														 rotate={2}>{activeProfile[colorMode + 'Surname']}</Bar>
											</Flex>
											{profileDescription ? (
													<Text fontSize={1} sx={{ whiteSpace: 'pre-wrap' }}>
														{profileDescription}
													</Text>
												) : null
											}
										</Flex>
									</Flex>
								)}
							</Modal>
						</>
					),
				]}
			/>
		</>
	)
};

export default IndexPage;

export const query = graphql`
    query {
        quiSommesNous: nodeQuiSommesNous {
            description: field_description_brut
            subtitle: field_sub_title
            title
            description_dark: field_dark_description_brut
            subtitle_dark: field_dark_sub_title
            title_dark: field_dark_titre
        }
        profiles: allNodeProfile {
            items: nodes {
                darkName: field_name_dark
                defaultName: field_name_light
                darkSurname: field_surname_dark
                defaultSurname: field_surname_light
                langcode
                id
                relationships {
                    darkPhoto: field_photo_dark {
                        relationships {
                            field_media_image {
                                localFile {
                                    childImageSharp {
                                        fluid(maxWidth: 680, quality: 80) {
                                            ...GatsbyImageSharpFluid_withWebp
                                        }
                                    }
                                }
                            }
                        }
                    }
                    defaultPhoto: field_photo_light {
                        relationships {
                            field_media_image {
                                localFile {
                                    childImageSharp {
                                        fluid(maxWidth: 680, quality: 80) {
                                            ...GatsbyImageSharpFluid_withWebp
                                        }
                                    }
                                }
                            }
                        }
                    }
                    darkDescription: field_description_profile_dark {
                        field_j_ai_ete
                    }
                    defaultDescription: field_description_profile_light {
                        field_j_ai_ete
                    }
                }
            }
        }
        profileSorting: configPagesProfileSorting {
            nodes: relationships {
                data: field_profile_display_order {
                    id
                }
            }
        }
        parallaxWhite: configsJson(name: {eq: "qui-sommes-nous-white"}) {
            layers {
                depth
                overflow
                image {
                    id
                    childImageSharp {
                        original {
                            width
                            height
                        }
                        fluid(maxWidth: 1920, quality: 50) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        parallaxBlack: configsJson(name: {eq: "qui-sommes-nous-black"}) {
            layers {
                depth
                overflow
                sx {
                    transform
                }
                animation {
                    pose {
                        rotate
                        x
                        y
                    }
                    keyframes {
                        duration
                        values
                        times
                    }
                    styles {
                        transformOrigin
                    }
                }
                image {
                    id
                    childImageSharp {
                        original {
                            width
                            height
                        }
                        fluid(maxWidth: 1920, quality: 50) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        parallaxMobile: configsJson(name: {eq: "qui-sommes-nous-mobile"}) {
            layers {
                depth
                overflow
                image {
                    id
                    childImageSharp {
                        original {
                            width
                            height
                        }
                        fluid(maxWidth: 1280, quality: 50) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        ogImage: file(name: {eq: "og_qui-sommes-nous"}) {
            ...ogImageFragment
        }
    }
`
